import { TEMPLATE_CATEGORIES, TEMPLATE_INDUSTRIES } from './templateTaxonomy';
/**
 * Templates de página: una lista ordenada de bloques, no un lienzo.
 *
 * Como en Relume, una página se arma con bloques de la biblioteca. El template
 * guarda sólo QUÉ bloque va en cada sitio —categoría y id—, así que arreglar un
 * bloque arregla todas las páginas que lo usan, y «Otra variante» sigue
 * funcionando sobre cada sección.
 *
 * Aquí sí caben el giro (`bestFor`) y el tipo de página: la composición de una
 * portada de restaurante no es la de un SaaS. Los bloques no los llevan.
 *
 * Se guarda en `templates_meta` con `kind: 'page'`: el tipo de página en la
 * columna `category`, el giro en `facets.bestFor` y la receta en
 * `templates_data.published_data.recipe`.
 */
export const PAGE_TYPES = [
    { value: 'home', ai: 'Main landing page of the site.' },
    { value: 'about', ai: 'Who the business is: story, values, team, numbers.' },
    { value: 'services', ai: 'What the business offers and how it works.' },
    { value: 'pricing', ai: 'Plans or prices, with reassurance to buy.' },
    { value: 'contact', ai: 'How to reach or book the business.' },
    { value: 'landing', ai: 'One focused campaign or product page that drives one action.' },
    { value: 'portfolio', ai: 'Selected work, projects or case studies.' },
];
const PAGE_TYPE_VALUES = new Set(PAGE_TYPES.map((type) => type.value));
const CATEGORY_VALUES = new Set(TEMPLATE_CATEGORIES.map((option) => option.value));
const INDUSTRY_VALUES = new Set(TEMPLATE_INDUSTRIES.map((option) => option.value));
/** Una página nunca pasa de esto: más bloques ya no es una página, es un sitio. */
export const MAX_RECIPE_SECTIONS = 10;
export function isPageType(value) {
    return typeof value === 'string' && PAGE_TYPE_VALUES.has(value);
}
/**
 * La receta tal como se guardó, sin lo que no encaja: una categoría que la
 * taxonomía no conoce o un id vacío se descartan, y se corta al tope.
 */
export function normalizePageRecipe(raw) {
    const sections = raw?.sections;
    if (!Array.isArray(sections))
        return { sections: [] };
    return {
        sections: sections.flatMap((entry) => {
            const { category, templateId } = (entry ?? {});
            if (typeof category !== 'string' || !CATEGORY_VALUES.has(category))
                return [];
            if (typeof templateId !== 'string' || !templateId.trim())
                return [];
            return [{ category, templateId: templateId.trim() }];
        }).slice(0, MAX_RECIPE_SECTIONS),
    };
}
/** Los giros válidos de una lista cualquiera, sin repetir. */
export function normalizePageBestFor(raw) {
    const values = Array.isArray(raw) ? raw : [];
    return [...new Set(values.filter((value) => typeof value === 'string' && INDUSTRY_VALUES.has(value)))];
}
/**
 * La lista para el prompt: una línea por template. La IA elige por id y
 * escribe los textos en ese orden de categorías.
 *
 *   [3f2a…] home · for:restaurant/hospitality — hero > content > gallery > testimonials > cta
 */
export function describePageTemplatesForAi(entries) {
    return entries
        .map((entry) => {
        const bestFor = entry.bestFor.length ? ` · for:${entry.bestFor.join('/')}` : '';
        return `[${entry.id}] ${entry.pageType}${bestFor} — ${entry.sections.map((section) => section.category).join(' > ')}`;
    })
        .join('\n');
}
//# sourceMappingURL=pageTemplates.js.map